import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axiosAuth from "../axiosAuth";
import { GET_COLLECTION_DETAILS } from "./apiRoutes";

// =========================
// Interfaces (Updated)
// =========================

export interface CollectionDetailsItem {
  _id: string;
  invoiceId: number;
  schoolId: string;
  invoiceGenerationDate: string; // ISO Date string
  invoiceOwner: string;
  invoiceOwnerName: string;
  schoolName: string;
  crmRH: string | null;
  crmRM: string | null;
  crmRMName: string;
  invoiceAmount: number;
  dueDays: number;
  status: string;
  balanceAmount: number;
  lastPaymentDate: string;
  lastPaymentRemark: string;
  lastPaymentFinanceConfirmation: boolean;
}

export interface CollectionDetailsResponse {
  status: number;
  error: boolean;
  count: number;
  issues: any[];
  data: {
    items: CollectionDetailsItem[];
  };
}

// =========================
// Filter Parameters Interface
// =========================

export interface CollectionDetailsFilterParams {
  year?: string | number;
  month?: string | number;
  geoRegionId?: string;
  schoolName?: string;
  crmRH?: string;
  crmRM?: string;
  invoiceOwner?: string;
  status?: string;
  dueDays?: string | number;
  invoiceGenerationDate?: string;
}

// =========================
// Fetch Function
// =========================

const fetchCollectionDetails = async (
  filterParams?: CollectionDetailsFilterParams
): Promise<CollectionDetailsResponse> => {
  const params = new URLSearchParams();

  if (filterParams) {
    if (filterParams.year) params.append("year", String(filterParams.year));
    if (filterParams.month) params.append("month", String(filterParams.month));
    if (filterParams.geoRegionId) params.append("geoRegionId", filterParams.geoRegionId);
    if (filterParams.schoolName) params.append("schoolName", filterParams.schoolName);
    if (filterParams.crmRH) params.append("crmRH", filterParams.crmRH);
    if (filterParams.crmRM) params.append("crmRM", filterParams.crmRM);
    if (filterParams.invoiceOwner) params.append("invoiceOwner", filterParams.invoiceOwner);
    if (filterParams.status) params.append("status", filterParams.status);
    if (filterParams.dueDays) params.append("dueDays", String(filterParams.dueDays));
    if (filterParams.invoiceGenerationDate) 
      params.append("invoiceGenerationDate", filterParams.invoiceGenerationDate);
  }

  const url = params.toString() 
    ? `${GET_COLLECTION_DETAILS}?${params.toString()}`
    : GET_COLLECTION_DETAILS;

  const response = await axiosAuth.get(url);
  return response.data as CollectionDetailsResponse;
};

// =========================
// useGetCollectionDetailsQuery Hook
// =========================

export const useGetCollectionDetailsQuery = (
  filterParams?: CollectionDetailsFilterParams,
  options?: UseQueryOptions<CollectionDetailsResponse, Error>
) =>
  useQuery<CollectionDetailsResponse, Error>({
    queryKey: ["collection-details", filterParams],
    queryFn: () => fetchCollectionDetails(filterParams),
    ...options,
  });

export default useGetCollectionDetailsQuery;
